// src/shared/changelog/entryCopy.ts
/**
 * The copy rules for an entry's `title` and `description`, in one place.
 *
 * These used to live inside `scripts/verify-changelog-entries.mjs`, which was
 * fine while the linter was the only thing that applied them. CTX-456 adds a
 * second surface — the Reflector V2 release form, where the entry is authored —
 * and that form has to reject exactly what CI rejects. A second, approximate
 * implementation would produce the worst outcome available: copy that passes
 * the form and then blocks the release at promotion time, which is precisely
 * the failure CTX-455's rollback makes expensive.
 *
 * So the rules move here and both callers import them, message text included.
 * Sharing the *message* and not just the predicate is deliberate: the author
 * should read the same sentence wherever they hit the rule, or they will think
 * they are two different rules.
 *
 * ── Scope ───────────────────────────────────────────────────────────────────
 *
 * Only the two free-text fields. Everything else the linter checks — required
 * fields, the category set, the product folder, filename shape, `public` being
 * an explicit boolean — is about an entry *file* and has no meaning in a form
 * whose fields are already typed. Moving those here would be abstraction for
 * one caller.
 *
 * ── The other repo ──────────────────────────────────────────────────────────
 *
 * `chopper-backend` carries its own port of these rules, converged by hand in
 * CTX-348. This extraction does not unify the two repos and does not try to:
 * it collapses the *dashboard* side from two implementations to one. The
 * cross-repo pair stays a pair, and the docblock in the backend's copy is still
 * the thing that keeps them honest.
 */

/** A 60-character headline. Enforced where the text is written, not by the column. */
export const MAX_TITLE = 60;

/** Room for two sentences. Same: policy here, storage is wider on purpose. */
export const MAX_DESCRIPTION = 300;

export type EntryCopyField = "title" | "description";

type Rule = readonly [RegExp, string];

/**
 * Internal vocabulary that must never reach customer copy. Each pattern earned
 * its place by actually appearing in the history curated in CTX-195 — this is
 * not a hypothetical list.
 */
const DEV_SPEAK: readonly Rule[] = [
  [/\.(tsx?|jsx?|mjs|cjs)\b/i, "a file extension"],
  // The trailing \b has to sit outside the repeated group: `_` is a word
  // character, so `[a-z]+_[a-z]+\b` never matches a three-segment identifier
  // like `new_field_name` — the boundary it wants falls between two word chars.
  [/\b[a-z0-9]+(?:_[a-z0-9]+)+\b/, "snake_case"],
  [/[a-z][A-Za-z0-9_]*\(/, "call syntax — put a space before a parenthetical"],
  [/\bsrc\//i, "a source path"],
  // \w* rather than s?, so refactored / refactoring is caught too. `modal` and
  // `refactor` stay here: they are unambiguously internal in customer copy,
  // with no legitimate reading.
  [/\b(modal|refactor)\w*\b/i, "an implementation word"],
];

/**
 * Same idea, enforced on `title` only.
 *
 * `component` and `endpoint` have legitimate customer readings — "Each network
 * component now reports its own health" is correct copy, and an entry under
 * `platform-api` may well need to name an endpoint. Rejecting them everywhere
 * makes the linter wrong more often than the author, which is how a gate loses
 * credibility.
 *
 * Keeping them on `title` preserves the intent — a 60-character headline has no
 * room for implementation nouns — without vetoing a whole sentence.
 *
 * This mirrors chopper-backend's copy exactly (CTX-348). The two validators
 * must accept and reject the same file: the contract is committed
 * byte-identical between the repos precisely so that an entry valid in one is
 * valid in the other, and a heuristic that disagrees breaks that quietly — the
 * author only finds out when the other repo's CI rejects their wording.
 */
const DEV_SPEAK_TITLE_ONLY: readonly Rule[] = [
  [
    /\b(endpoint|component)\w*\b/i,
    "an implementation word (allowed in description, not here)",
  ],
];

const MAX_BY_FIELD: Record<EntryCopyField, number> = {
  title: MAX_TITLE,
  description: MAX_DESCRIPTION,
};

function rulesFor(field: EntryCopyField): readonly Rule[] {
  return field === "title" ? [...DEV_SPEAK, ...DEV_SPEAK_TITLE_ONLY] : DEV_SPEAK;
}

/**
 * The first internal-vocabulary hit, or null.
 *
 * First and not all of them, matching the linter: a title tripping three
 * patterns is one problem to the author, and listing three is a wall of text
 * that reads like the gate is piling on.
 */
export function findDevSpeak(
  field: EntryCopyField,
  value: string
): { what: string; match: string } | null {
  for (const [pattern, what] of rulesFor(field)) {
    const match = value.match(pattern);
    if (match) return { what, match: match[0] };
  }
  return null;
}

/**
 * Every problem with one field's copy, worded exactly as CI words it.
 *
 * Length first, then vocabulary — both can fire on the same value, and that
 * order puts the mechanical problem before the judgement one.
 */
export function checkEntryCopy(
  field: EntryCopyField,
  value: string
): string[] {
  const problems: string[] = [];

  const max = MAX_BY_FIELD[field];
  if (value.length > max) {
    problems.push(`${field} is ${value.length} chars, over the ${max} limit.`);
  }

  const devSpeak = findDevSpeak(field, value);
  if (devSpeak) {
    problems.push(
      `${field} contains ${devSpeak.what} ("${devSpeak.match}"). ` +
        `Write it the way you would to a customer.`
    );
  }

  return problems;
}
